import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cn } from '../cn'
import { Cross2Icon } from '@radix-ui/react-icons'
import { FocusScope } from '@radix-ui/react-focus-scope'
import { getModalZIndex } from '../../../styles/z-index'

// Context to track modal nesting depth
export const ModalDepthContext = React.createContext(0)

export const useModalDepth = () => React.useContext(ModalDepthContext)

// Wrapper to increment depth for nested modals
const Dialog = ({ children, ...props }: DialogPrimitive.DialogProps) => {
  const currentDepth = useModalDepth()
  const nextDepth = React.useMemo(() => currentDepth + 1, [currentDepth])

  return (
    <DialogPrimitive.Root {...props}>
      <ModalDepthContext.Provider value={nextDepth}>
        {children}
      </ModalDepthContext.Provider>
    </DialogPrimitive.Root>
  )
}

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

// NOTE: updated to use div vs RadixUI DialogOverlay, and added 'pointer-events-auto'
// to prevent scrolling issues (see https://github.com/radix-ui/primitives/issues/1128#issuecomment-2196393024)
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  const depth = useModalDepth()
  const zIndex = React.useMemo(() => getModalZIndex(depth), [depth])

  return (
    <div
      ref={ref}
      className={cn(
        'fixed inset-0 pointer-events-auto bg-overlay/50 backdrop-blur-xs data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className
      )}
      style={{ zIndex }}
      {...props}
    />
  )
})
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

export type DialogContentSize = 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full'
const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    size?: DialogContentSize
    noPadding?: boolean
    disableCloseIcon?: boolean
  }
>(
  (
    { size, className, children, noPadding, disableCloseIcon, ...props },
    ref
  ) => {
    const depth = useModalDepth()
    const zIndex = React.useMemo(() => getModalZIndex(depth), [depth])

    return (
      <DialogPortal>
        <DialogOverlay />
        <FocusScope trapped={false}>
          <DialogPrimitive.Content
            ref={ref}
            onPointerDownOutside={(e) => e.preventDefault()}
            className={cn(
              'fb-modal fixed left-[50%] top-[50%] flex flex-col w-full max-w-xl max-h-[90vh] overflow-hidden translate-x-[-50%] translate-y-[-50%] gap-3 border bg-white py-4 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
              {
                'max-w-4xl': size === 'lg',
                'max-w-6xl': size === 'xl',
                'max-w-7xl': size === '2xl',
                'max-w-8xl': size === '3xl',
                'max-w-[95vw]': size === 'full',
                'px-8': !noPadding,
              },
              className
            )}
            style={{ zIndex }}
            {...props}
          >
            {children}
            {!disableCloseIcon && (
              <DialogPrimitive.Close className="absolute right-6 top-6 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
                <Cross2Icon className="h-6 w-6" />
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
            )}
          </DialogPrimitive.Content>
        </FocusScope>
      </DialogPortal>
    )
  }
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  noPadding,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  noPadding?: boolean
}) => (
  <div
    className={cn(
      'flex flex-col gap-2 space-y-1.5 sm:text-left pr-6',
      {
        'px-8': noPadding,
      },
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({
  className,
  noPadding,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  noPadding?: boolean
}) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 py-2',
      {
        'px-8': noPadding,
      },
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-2xl font-semibold leading-none mb-2', className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-gray-600 font-normal', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
